import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import FinalDecisionModal from './modals/FinalDecisionModal';
import ChatBox from './modals/ChatBox';
import SignatureApplicationModal from '../pages/modals/SignatureApplicationModal';

import './ApplicationsList.css';
import styles from './InExpertiseApplicationsList.module.css';

export default function InExpertiseApplicationsList({ locale }) {
  const token = localStorage.getItem('token');

  function norm(s) {
    return String(s ?? '').toLowerCase().trim();
  }
  function gid(g, idx) {
    return String(g?.group_id ?? g?.id ?? norm(g?.group_title ?? g?.title) ?? idx);
  }

  const t = {
    kz: {
      title: 'Оқу басылымы',
      view: 'Толық көру',
      sign: 'Қол қою',
      empty: 'Сараптамада Өтінімдер табылмады',
      approve: 'Қабылдау',
      reject: 'Қайтару',
      comment: 'Түсіндірме (міндетті):',
      confirmReject: 'Қайтару',
      grade: 'Сынып',
      subject: 'Пән',
      date: 'Күні',
      status: 'Статус',
      mistakesFile: 'Қателер файлы',
      decisionFile: 'Шешім файлы',
      chatWithTeacher: 'Құжатты келісу парағы',
      searchPlaceholder: 'Топ атауы бойынша іздеу…',
      filters: 'Фильтрлер',
      subjects: 'Пәндер',
      clear: 'Тазарту',
      noGroups: 'Сәйкес топтар табылмады',
      expandAll: 'Барлығын ашу',
      collapseAll: 'Барлығын жабу',
      tableHeaders: {
        expert: 'Сарапшы',
        subject: 'Пән',
        status: 'Күйі',
        averageScore: 'Орташа балл',
        finalAverage: 'Қорытынды орташа балл',
        summaryDecision: 'Қорытынды шешім',
        actionsTitle: 'Қорытынды құжат бойынша әрекеттер',
      },
      modal: {
        finalTable: {
          title: 'Қорытынды құжаттар тізімі',
          file: 'Файл',
          avgScore: 'Орташа балл',
          verdict: 'Шешім',
          created: 'Құру күні',
          status: 'Күйі',
          reason: 'Түсіндірме',
        },
        uploadTitle: 'Қорытындыны жүктеу',
        selectFile: 'Файлды таңдаңыз',
        sendAndGenerate: 'Жіберу және құжатты генерациялау',
        error: 'Қате орын алды',
        convertToPdf: 'PDF-ке түрлендіру',
        downloadPdf: 'PDF жүктеу',
        pdfAttachment: 'Қосымша PDF',
      },
      statusMap: { accepted: 'Қабылданды', rejected: 'Қайтарылды', pending: 'Күтуде' },
    },
    ru: {
      title: 'Учебное издание',
      view: 'Просмотр',
      sign: 'Подписать',
      empty: 'Заявок на экспертизе не найдено',
      approve: 'Принять',
      reject: 'Отклонить',
      comment: 'Комментарий (обязательно):',
      confirmReject: 'Отклонить',
      grade: 'Класс',
      subject: 'Предмет',
      date: 'Дата',
      status: 'Статус',
      mistakesFile: 'Файл ошибок',
      decisionFile: 'Файл решения',
      chatWithTeacher: 'Лист согласования документа',
      searchPlaceholder: 'Поиск по названию группы…',
      filters: 'Фильтры',
      subjects: 'Предметы',
      clear: 'Сбросить',
      noGroups: 'Подходящих групп не найдено',
      expandAll: 'Открыть всё',
      collapseAll: 'Закрыть всё',
      tableHeaders: {
        expert: 'Эксперт',
        subject: 'Предмет',
        status: 'Статус',
        averageScore: 'Средний балл',
        finalAverage: 'Итоговый балл',
        summaryDecision: 'Итоговое решение',
        actionsTitle: 'Действия с финальным документом',
      },
      modal: {
        finalTable: {
          title: 'Список финальных документов',
          file: 'Файл',
          avgScore: 'Средний балл',
          verdict: 'Решение',
          created: 'Дата создания',
          status: 'Статус',
          reason: 'Комментарий',
        },
        uploadTitle: 'Загрузка финального документа',
        selectFile: 'Выберите файл',
        sendAndGenerate: 'Отправить и сгенерировать',
        error: 'Произошла ошибка',
        convertToPdf: 'Конвертировать в PDF',
        downloadPdf: 'Скачать PDF',
        pdfAttachment: 'PDF вложение',
      },
      statusMap: { accepted: 'Принят', rejected: 'Отклонён', pending: 'В ожидании' },
    },
  }[locale];

  function toStatusKey(raw) {
    const s = (raw || '').toString().toLowerCase().trim();
    if (['ожидание_руководителя','ожидание руководителя','бөлім басшысын күту'].includes(s)) return 'waiting_head';
    if (['қабылданды','принят','accepted'].includes(s)) return 'accepted';
    if (['қайтарылды','отклонён','отклонен','rejected'].includes(s)) return 'rejected';
    if (['күтуде','в ожидании','pending'].includes(s)) return 'pending';
    if (['дайын','готово','ready'].includes(s)) return 'ready';
    if (['жоба','черновик','draft'].includes(s)) return 'draft';
    if (['қол қоюға дайын','готов к подписи','ready_signing'].includes(s)) return 'ready_signing';
    if (['қол қоюда','на подписи','signing'].includes(s)) return 'signing';
    if (['қорытындылау','итог','итоги','на подведении итогов'].includes(s)) return 'in_summary';
    if (['өңдеуді талап етеді','требует доработки','требует корректировки'].includes(s)) return 'needs_rework';
    return 'pending';
  }

  function statusLabel(raw) {
    const k = toStatusKey(raw);
    return STATUS_LABELS[k]?.[locale === 'kz' ? 'kz' : 'ru'] || raw || '—';
  }

  const STATUS_LABELS = {
    waiting_head: { kz: 'Бөлім басшысын күту', ru: 'Ожидание руководителя' },
    accepted:     { kz: 'Қабылданды',          ru: 'Принят' },
    rejected:     { kz: 'Қайтарылды',          ru: 'Отклонён' },
    pending:      { kz: 'Күтуде',              ru: 'В ожидании' },
    ready:        { kz: 'Дайын',               ru: 'Готово' },
    draft:        { kz: 'Жоба',                ru: 'Черновик' },
    ready_signing:{ kz: 'Қол қоюға дайын',     ru: 'Готов к подписи' },
    signing:      { kz: 'Қол қоюда',           ru: 'На подписи' },
    in_summary:   { kz: 'Қорытындылау',        ru: 'На подведении итогов' },
    needs_rework: { kz: 'Өңдеуді талап етеді', ru: 'Требует доработки' },
  };

  function toDecisionKey(raw) {
    const s = (raw || '').toString().toLowerCase().trim();
    if (['өңдеуді талап етеді','требует доработки','корректировка'].includes(s)) return 'needs_rework';
    if (['қабылданды','принят','accepted'].includes(s)) return 'accepted';
    if (['қайтарылды','отклонён','отклонен','rejected'].includes(s)) return 'rejected';
    return null;
  }

  function decisionLabel(raw) {
    const k = toDecisionKey(raw);
    if (!k) return raw || '—';
    return DECISION_LABELS[k][locale === 'kz' ? 'kz' : 'ru'];
  }

  const DECISION_LABELS = {
    needs_rework: { kz: 'Өңдеуді талап етеді', ru: 'Требует доработки' },
    accepted:     { kz: 'Қабылданды',          ru: 'Принят' },
    rejected:     { kz: 'Қайтарылды',          ru: 'Отклонён' },
  };

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [profile, setProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [meta, setMeta] = useState(null);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [experts, setExperts] = useState([]);
  const [finalDocuments, setFinalDocuments] = useState([]);
  const [finalAverage, setFinalAverage] = useState('');
  const [summaryDecision, setSummaryDecision] = useState('');

  const [finalDoc, setFinalDoc] = useState(null);
  const [comment, setComment] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [finalUploaded, setFinalUploaded] = useState(false);
  const [finalFile, setFinalFile] = useState(null);

  const [showChatBox, setShowChatBox] = useState(false);
  const [scientistDecisionsMap, setScientistDecisionsMap] = useState({});
  const currentScientistDecisions = scientistDecisionsMap[selectedApplication?.id] || [];
  const chatLang = locale === 'kz' ? 'kk' : 'ru';

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureApp, setSignatureApp] = useState(null);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [signatures, setSignatures] = useState([]);

  const EXP_KEY = 'ieal_expanded_groups';
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem(EXP_KEY) || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem(EXP_KEY, JSON.stringify(expanded));
  }, [expanded]);

  const FILTERS_KEY = 'ieal_filters';
  const params = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(() => {
    try { return params.get('q') || JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}').q || ''; }
    catch { return ''; }
  });
  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState(() => {
    try {
      const fromURL = params.get('s');
      if (fromURL) return fromURL.split(',').filter(Boolean);
      return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}').subjects || [];
    } catch { return []; }
  });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    q ? p.set('q', q) : p.delete('q');
    selectedSubjects.length ? p.set('s', selectedSubjects.join(',')) : p.delete('s');
    window.history.replaceState(null, '', `${window.location.pathname}?${p.toString()}`);
    localStorage.setItem(FILTERS_KEY, JSON.stringify({ q, subjects: selectedSubjects }));
  }, [q, selectedSubjects]);

  useEffect(() => {
    (async () => {
      try {
        const [appsRes, profileRes] = await Promise.all([
          axios.get('/expertise/grouped', {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          }),
          axios.get('/internal/profile', { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const rawGroups = appsRes.data?.data || [];
        const normalizedGroups = rawGroups.map((g) => ({
          ...g,
          group_id:    g.group_id    ?? g.id,
          group_title: g.group_title ?? g.title,
          applications: (g.applications || []).map((app) => ({
            ...app,
            title:   app.title   ?? g.title,
            subject: app.subject ?? g.subject,
            grade:   app.grade   ?? g.grade,
          })),
        }));

        setGroups(normalizedGroups);
        setMeta(appsRes.data?.meta || null);
        setProfile(profileRes.data);
        setCurrentUserId(profileRes.data?.id);
      } catch (e) {
       
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const allSubjects = useMemo(() => {
    const set = new Set();
    (groups || []).forEach((g) => {
      if (g.subject) set.add(g.subject);
      (g.applications || []).forEach((a) => { if (a.subject) set.add(a.subject); });
    });
    return Array.from(set).sort();
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const qn = norm(q);
    return (groups || [])
      .map((g) => {
        const apps = (g.applications || []).filter((a) => {
          const appSubject = a.subject ?? g.subject;
          const subjOk = selectedSubjects.length ? selectedSubjects.includes(appSubject) : true;
          return subjOk;
        });
        const groupTitle = g.group_title ?? g.title ?? '';
        const titleOk = !qn || norm(groupTitle).includes(qn);
        if (titleOk && apps.length) return { ...g, applications: apps };
        return null;
      })
      .filter(Boolean);
  }, [groups, q, selectedSubjects]);

  const isOpen = (g, idx) => {
    const id = gid(g, idx);
    if (expanded[id] === undefined) return idx === 0;
    return !!expanded[id];
  };
  const toggleGroup = (g, idx) => {
    const id = gid(g, idx);
    setExpanded((prev) => ({ ...prev, [id]: !isOpen(g, idx) }));
  };
  const allOpen = useMemo(() => {
    return (filteredGroups || []).every((g, i) => {
      const id = gid(g, i);
      const v = expanded[id];
      return v === undefined ? i === 0 : !!v;
    });
  }, [filteredGroups, expanded]);
  const toggleAll = () => {
    const next = { ...expanded };
    (filteredGroups || []).forEach((g, i) => { next[gid(g, i)] = !allOpen; });
    setExpanded(next);
  };

  const [pdfBusy, setPdfBusy] = useState({});

  const getSafeFilename = (path, fallback) => {
    if (!path) return fallback;
    const name = String(path).split('/').pop();
    return name || fallback;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const convertFinalDocToPdf = async (docId) => {
    try {
      setPdfBusy((m) => ({ ...m, [docId]: true }));
      const res = await axios.get(`/final-docs/${docId}/convert`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const disp = res.headers?.['content-disposition'];
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(disp || '');
      const filename = (m && decodeURIComponent(m[1] || m[2] || '')) || `finaldoc_${docId}.pdf`;
      downloadBlob(res.data, filename);
      if (selectedApplication?.id) {
        const docsRes = await axios.get(`/expertise/internal/final-documents/${selectedApplication.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFinalDocuments(docsRes.data?.documents || []);
      }
    } catch (e) {
    
      alert(t?.modal?.error || 'Қате / Ошибка');
    } finally {
      setPdfBusy((m) => ({ ...m, [docId]: false }));
    }
  };

  const downloadFinalDocPdf = async (doc) => {
    if (!doc?.pdf_file_path) return convertFinalDocToPdf(doc.id);
    try {
      setPdfBusy((m) => ({ ...m, [doc.id]: true }));
      const res = await axios.get(`/storage/${doc.pdf_file_path}`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      downloadBlob(res.data, getSafeFilename(doc.pdf_file_path, `finaldoc_${doc.id}.pdf`));
    } catch (e) {
      return convertFinalDocToPdf(doc.id);
    } finally {
      setPdfBusy((m) => ({ ...m, [doc.id]: false }));
    }
  };

  const downloadFinalAttachmentPdf = async (doc) => {
    if (!doc?.pdf_final_attachment) { alert('PDF жоқ / Нет PDF вложения'); return; }
    try {
      setPdfBusy((m) => ({ ...m, [doc.id]: true }));
      const res = await axios.get(`/storage/${doc.pdf_final_attachment}`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      downloadBlob(res.data, getSafeFilename(doc.pdf_final_attachment, `finaldoc_${doc.id}_attachment.pdf`));
    } catch (e) {

      alert(t?.modal?.error || 'Қате / Ошибка');
    } finally {
      setPdfBusy((m) => ({ ...m, [doc.id]: false }));
    }
  };

  const refreshScientistDecisions = async (appId) => {
    const id = appId || selectedApplication?.id;
    if (!id) return;
    try {
      const res = await axios.get(`/external-expert/scientist-decisions/by-application/${id}/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data || [];
      setScientistDecisionsMap((prev) => ({ ...prev, [id]: data }));
    } catch (e) {
     
    }
  };

  const getFilenameFromDisposition = (disposition) => {
    if (!disposition) return null;
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(disposition);
    try { return decodeURIComponent(m?.[1] || m?.[2] || ''); }
    catch { return m?.[1] || m?.[2] || 'file.docx'; }
  };

  const downloadFinalDoc = async (applicationId) => {
    try {
      const res = await axios.get(`/external-expert/final-docs/${applicationId}/download`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const filename = getFilenameFromDisposition(res.headers['content-disposition']) || `final_document_${applicationId}.docx`;
      downloadBlob(new Blob([res.data]), filename);
    } catch (e) {
     
      alert(t?.modal?.error || 'Жүктеу қатесі');
    }
  };

  const downloadFinalAttachment = async (applicationId) => {
    try {
      const res = await axios.get(`/external-expert/final-docs/${applicationId}/attachment`, {
        responseType: 'blob', headers: { Authorization: `Bearer ${token}` },
      });
      const filename = getFilenameFromDisposition(res.headers['content-disposition']) || `final_attachment_${applicationId}.docx`;
      downloadBlob(new Blob([res.data]), filename);
    } catch (e) {
    
      alert(t?.modal?.error || 'Жүктеу қатесі');
    }
  };

  const openApplication = async (app) => {
    setSelectedApplication(app);
    try {
      const res = await axios.get(`/expertise/internal/applications/${app.id}/external-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExperts(res.data?.experts || []);
      setFinalAverage(res.data?.final_average || '');
      setSummaryDecision(res.data?.summary_decision || '');
      await refreshScientistDecisions(app.id);

      const docsRes = await axios.get(`/expertise/internal/final-documents/${app.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinalDocuments(docsRes.data?.documents || []);
    } catch (e) {
    
    }
  };

  const openSignature = async (app) => {
    setSignatureApp(app);
    try {
      const res = await axios.get(`/expertise/internal/applications/${app.id}/external-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinalAverage(res.data?.final_average || '');
      setSummaryDecision(res.data?.summary_decision || '');

      const docsRes = await axios.get(`/expertise/internal/final-documents/${app.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const firstFinalDoc = docsRes.data?.documents?.[0];
      if (!firstFinalDoc) {
        alert('❌ Финалдық документ табылмады');
        setShowSignatureModal(false);
        return;
      }

      const base64Res = await axios.get(`/final-documents/${firstFinalDoc.id}/base64`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const signersRes = await axios.get(`/final-documents/${firstFinalDoc.id}/signatures/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSignatures(signersRes.data?.signers || []);
      setPendingSignatures(signersRes.data?.not_signed || []);
      setFinalDoc(firstFinalDoc);
      setFinalFile({ filename: base64Res.data.filename, base64: base64Res.data.base64 });
      setShowSignatureModal(true);
    } catch (e) {
   
      alert('Қол қою деректерін жүктеу қатесі');
      setShowSignatureModal(true);
    }
  };

  const getMyActiveDecisionObj = () => {
    const list = scientistDecisionsMap[selectedApplication?.id] || [];
    const pendingForMe = list.find((x) =>
      (x.reviews || []).some((r) => String(r.reviewer_id) === String(currentUserId) && !r.decision)
    );
    if (pendingForMe) return pendingForMe;
    const sent = list.find((x) => ['жіберілді', 'отправлено'].includes(norm(x?.decision?.status)));
    if (sent) return sent;
    return list[0] || null;
  };
  const getMyReviewEntry = (latest) => {
    if (!latest) return null;
    return (latest.reviews || []).find((r) => String(r.reviewer_id) === String(currentUserId)) || null;
  };
  const latestForMe = getMyActiveDecisionObj();
  const mySlot = getMyReviewEntry(latestForMe);
  const alreadyReviewedMe = !!(mySlot?.decision || (mySlot?.comment && mySlot.comment.trim()));

  const downloadMistakesFile = async (applicationId, expertId) => {
    try {
      const res = await axios.get(
        `/expertise/internal/applications/${applicationId}/external-experts/${expertId}/mistakes/download`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      downloadBlob(new Blob([res.data]), `қате_файлы_${expertId}.docx`);
    } catch (e) {
   
      alert('Қате файлды жүктеу мүмкін болмады');
    }
  };

  const downloadDecisionFile = async (applicationId, expertId) => {
    try {
      const res = await axios.get(
        `/expertise/internal/applications/${applicationId}/external-experts/${expertId}/decision/download`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      downloadBlob(new Blob([res.data]), `шешім_файлы_${expertId}.docx`);
    } catch (e) {

      alert('Шешім файлды жүктеу мүмкін болмады');
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`/expertise/department-head/final-documents/${finalDoc.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('✅ Құжат қабылданды');
      setSelectedApplication(null);
      setFinalDoc(null);
      setShowChatBox(false);
    } catch {
      alert('❌ Қабылдау қатесі');
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) return alert('Комментарий қажет');
    try {
      await axios.post(
        `/expertise/department-head/final-documents/${finalDoc.id}/reject`,
        { reason: comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('❌ Құжат қайтарылды');
      setSelectedApplication(null);
      setFinalDoc(null);
      setShowChatBox(false);
    } catch {
      alert('Қайтару қатесі');
    }
  };

  // роль руководителя отдела
  const roles = profile?.roles || [];
  const canViewChat = roles.includes('department_supervisor');

  if (loading) return <p>Жүктелуде...</p>;
  if (!filteredGroups.length) return <p>{t.noGroups}</p>;

  return (
    <>
      <div className={styles.iealToolbar}>
        <input
          className={styles.iealSearch}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.searchPlaceholder}
        />

        <div className={styles.iealFilters}>
          <button className={styles.iealAButton} onClick={() => setSubjectsOpen((v) => !v)}>
            {t.filters}{selectedSubjects.length ? ` (${selectedSubjects.length})` : ''}
          </button>

          {subjectsOpen && (
            <div className={styles.iealFilterPanel}>
              <div className={styles.iealFilterHeader}>
                <span>{t.subjects}</span>
                <button className={styles.iealClearBtn} onClick={() => setSelectedSubjects([])}>{t.clear}</button>
              </div>
              <div className={styles.iealChips}>
                {allSubjects.map((s) => {
                  const active = selectedSubjects.includes(s);
                  return (
                    <button
                      key={s}
                      className={`${styles.iealChip} ${active ? styles.iealChipActive : ''}`}
                      onClick={() =>
                        setSelectedSubjects((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])
                      }
                      title={s}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <button className={styles.iealAButton} onClick={toggleAll}>
          {allOpen ? t.collapseAll : t.expandAll}
        </button>
      </div>

      {filteredGroups.map((group, gIdx) => {
        const open = isOpen(group, gIdx);
        const appsCount = group.applications?.length || 0;
        const groupTitle = group.group_title ?? group.title ?? '—';

        return (
          <div key={gid(group, gIdx)} className="group-block">
            <button
              type="button"
              className={styles.iealAccHeader}
              onClick={() => toggleGroup(group, gIdx)}
              aria-expanded={open}
            >
              <span className={styles.iealChev}>{open ? '▾' : '▸'}</span>
              <span className={styles.iealGTitle}>{groupTitle}</span>
              <span className={styles.iealBadge}>{appsCount}</span>
            </button>

            {open && (
              <div className={styles.iealAccPanel}>
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>{t.title}</th>
                      <th>{t.grade}</th>
                      <th>{t.subject}</th>
                      <th>{t.status}</th>
                      <th>{t.date}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(group.applications || []).map((app) => {
                      const appTitle   = app.title   ?? group.title   ?? '—';
                      const appGrade   = app.grade   ?? group.grade   ?? '—';
                      const appSubject = app.subject ?? group.subject ?? '—';

                      return (
                        <tr key={app.id}>
                          <td>{appTitle}</td>
                          <td>{appGrade}</td>
                          <td>{appSubject}</td>
                          <td>{app.status || '—'}</td>
                          <td>{new Date(app.created_at).toLocaleDateString()}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button className="btn-view" onClick={() => openApplication(app)}>
                                {t.view}
                              </button>
                              <button
                                className="btn-view"
                                style={{ backgroundColor: '#4caf50' }}
                                onClick={() => openSignature(app)}
                              >
                                {t.sign}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Модалка приложения (Толық көру) */}
      {selectedApplication && (
        <div className={styles.iealModalOverlay}>
          <div className={styles.iealModalInner}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{selectedApplication.title ?? selectedApplication.group_title ?? '—'}</h3>
              <div className={styles.headerButtons}>
                {canViewChat && (
                  <button
                    onClick={async () => {
                      await refreshScientistDecisions(selectedApplication.id);
                      setShowChatBox(true);
                    }}
                    className={styles.chatButton}
                    title={t.chatWithTeacher}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                    </svg>
                    <span>{t.chatWithTeacher}</span>
                  </button>
                )}
                <button
                  onClick={() => { setShowChatBox(false); setSelectedApplication(null); }}
                  className={styles.closeBtn}
                  title="Жабу / Закрыть"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full border rounded-md shadow-sm bg-white">
                <thead className="bg-gray-100">
                  <tr className="text-left">
                    <th className="px-4 py-2 border">{t.tableHeaders.expert}</th>
                    <th className="px-4 py-2 border">{t.tableHeaders.subject}</th>
                    <th className="px-4 py-2 border">{t.tableHeaders.status}</th>
                    <th className="px-4 py-2 border">{t.tableHeaders.averageScore}</th>
                    <th className="px-4 py-2 border">{t.mistakesFile}</th>
                    <th className="px-4 py-2 border">{t.decisionFile}</th>
                  </tr>
                </thead>
                <tbody>
                  {experts.map((exp, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border">{exp.name}</td>
                      <td className="px-4 py-2 border">{exp.subject || '—'}</td>
                      <td className="px-4 py-2 border">
                        <span className={`${styles.iealStatus} ${styles['iealStatus_' + toStatusKey(exp.status)]}`}>
                          {statusLabel(exp.status)}
                        </span>
                      </td>
                      <td className="px-4 py-2 border">{exp.score}</td>
                      <td className="px-4 py-2 border text-center">
                        {exp.has_mistakes_file ? (
                          <button onClick={() => downloadMistakesFile(selectedApplication.id, exp.id)} className={styles.iealBtnMistakes}>
                            {t.mistakesFile}
                          </button>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2 border text-center">
                        {exp.has_decision_file ? (
                          <button onClick={() => downloadDecisionFile(selectedApplication.id, exp.id)} className={styles.iealBtnDecision}>
                            {t.decisionFile}
                          </button>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-center">
              <p className="text-lg"><strong>{t.tableHeaders.finalAverage}:</strong> {finalAverage}</p>
              <p className="text-lg"><strong>{t.tableHeaders.summaryDecision}:</strong> {decisionLabel(summaryDecision)}</p>
            </div>

            {finalDocuments.length > 0 && (
              <div className={styles.iealSection}>
                <h4 className={styles.iealSectionTitle}>📄 {t.modal.finalTable.title}</h4>
                <table className={styles.iealTable}>
                  <thead>
                    <tr>
                      <th>📁 {t.modal.finalTable.file}</th>
                      <th>📊 {t.modal.finalTable.avgScore}</th>
                      <th>📌 {t.modal.finalTable.verdict}</th>
                      <th>📅 {t.modal.finalTable.created}</th>
                      <th>📂 {t.modal.finalTable.status}</th>
                      <th>📝 {t.modal.finalTable.reason}</th>
                      <th>⚙️</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalDocuments.map((doc) => {
                      const k = toStatusKey(doc.status);
                      const label = statusLabel(doc.status);
                      return (
                        <tr key={doc.id}>
                          <td>
                            <div className={styles.iealFileBtns}>
                              <button
                                className={styles.iealDocBtn}
                                onClick={() => downloadFinalDoc(selectedApplication.id)}
                              >
                                <span className={styles.iealDocName}>
                                  {doc.file_path?.split('/').pop() || 'Жүктеу'}
                                </span>
                              </button>
                              {!!doc.final_attachment && (
                                <button
                                  className={styles.iealDocBtnSecondary}
                                  onClick={() => downloadFinalAttachment(selectedApplication.id)}
                                >
                                  <span className={styles.iealDocName}>{doc.final_attachment.split('/').pop()}</span>
                                </button>
                              )}
                            </div>
                          </td>
                          <td>{doc.average_score}</td>
                          <td>{decisionLabel(doc.verdict)}</td>
                          <td>{new Date(doc.created_at).toLocaleString()}</td>
                          <td>
                            <span className={`${styles.iealStatus} ${styles['iealStatus_' + k]}`}>{label}</span>
                          </td>
                          <td>{doc.rejection_reason || '—'}</td>
                          <td>
                            {toStatusKey(doc.status) === 'waiting_head' && (
                              <button onClick={() => setFinalDoc(doc)} className={styles.iealDecisionBtn}>
                                📝 {locale === 'ru' ? 'Решение' : 'Шешім'}
                              </button>
                            )}
                            {['draft', 'accepted'].includes(toStatusKey(doc.status)) && (
                              <>
                                {!doc.pdf_file_path ? (
                                  <button
                                    disabled={!!pdfBusy[doc.id]}
                                    onClick={() => convertFinalDocToPdf(doc.id)}
                                    className={styles.iealDecisionBtn}
                                    style={{ marginLeft: 8 }}
                                  >
                                    {pdfBusy[doc.id] ? '…' : '🖨️'} {t.modal.convertToPdf}
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      disabled={!!pdfBusy[doc.id]}
                                      onClick={() => downloadFinalDocPdf(doc)}
                                      className={styles.iealDecisionBtn}
                                      style={{ marginLeft: 8 }}
                                    >
                                      {pdfBusy[doc.id] ? '…' : '⬇️'} {t.modal.downloadPdf}
                                    </button>
                                    {doc.pdf_final_attachment && (
                                      <button
                                        disabled={!!pdfBusy[doc.id]}
                                        onClick={() => downloadFinalAttachmentPdf(doc)}
                                        className={styles.iealDocBtnSecondary}
                                        style={{ marginLeft: 8 }}
                                      >
                                        {pdfBusy[doc.id] ? '…' : '📎'} {t.modal.pdfAttachment}
                                      </button>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Финальное решение руководителя */}
      {finalDoc && selectedApplication && (
        <FinalDecisionModal
          selectedApplication={selectedApplication}
          finalDoc={finalDoc}
          onClose={() => { setFinalDoc(null); setComment(''); }}
          comment={comment}
          setComment={setComment}
          onApprove={handleApprove}
          onReject={handleReject}
          t={t}
          locale={locale}
        />
      )}

      {/* Модалка подписи */}
{showSignatureModal && signatureApp && (
  <SignatureApplicationModal
    application={signatureApp}
    onClose={() => { setShowSignatureModal(false); setSignatureApp(null); }}
    locale={locale}
    finalAverage={finalAverage}
    summaryDecision={summaryDecision}
    t={t}
    token={token}
    userId={currentUserId}
    finalDocumentId={finalDoc?.id}
    base64File={finalFile?.base64}
    base64Filename={finalFile?.filename}
    signatures={signatures}
    pendingSignatures={pendingSignatures}
    isDepartmentHead={true}
    onSignedSuccess={() => { setShowSignatureModal(false); setSignatureApp(null); }}
    onSignedFail={() => alert('Қол қою қатесі / Ошибка подписи')}
  />
)}

      {/* Чат */}
      {selectedApplication && showChatBox && (
        <ChatBox
          isOpen={showChatBox}
          onClose={() => setShowChatBox(false)}
          decisions={currentScientistDecisions}
          lang={chatLang}
          onDownloadDecisionFile={async (decisionId) => {
            try {
              const res = await axios.get(`/external-expert/scientist-decisions/${decisionId}/download`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` },
              });
              downloadBlob(new Blob([res.data]), `scientist-decision-${decisionId}.docx`);
            } catch (e) {
              alert(t.modal?.error || 'Қате / Ошибка');
            }
          }}
          currentUserId={currentUserId}
          isScientist={false}
          isDepartmentHead={canViewChat}
          allowUploadSend={false}
          alreadyReviewed={alreadyReviewedMe}
          applicationId={selectedApplication.id}
          token={token}
          onRefreshDecisions={() => refreshScientistDecisions(selectedApplication.id)}
          onSubmitReview={async (decision, commentText, reviewIdFromChild) => {
            const latest = getMyActiveDecisionObj();
            const reviewId = reviewIdFromChild || latest?.decision?.id;
            if (!reviewId) {
              alert(chatLang === 'kk' ? 'Қате: шешім табылмады' : 'Ошибка: не найден ID решения');
              return;
            }
            try {
              await axios.post(
                `/external-expert/scientist-decisions/${reviewId}/review`,
                { decision, comment: commentText },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              alert(chatLang === 'kk' ? '✅ Жауап сақталды' : '✅ Ответ сохранён');
              await refreshScientistDecisions(selectedApplication.id);
            } catch (err) {
              const status = err?.response?.status;
              if (status === 403) alert(chatLang === 'kk' ? '403: Рұқсат жоқ' : '403: Нет прав');
              else if (status === 401) alert(chatLang === 'kk' ? '401: Сессия аяқталды' : '401: Сессия истекла');
              else alert(chatLang === 'kk' ? 'Қате орын алды' : 'Произошла ошибка');
            }
          }}
        />
      )}
    </>
  );
}